/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { getGeoLocationFromIp } from './geolocation.api';
import type { UserLocation } from './geolocation.types';
import { getLocationWithFallback } from './location-utils';
import { getCachedLocation, setCachedLocation, CACHE_TTL_MS } from './location-cache';

/**
 * Requests the user's position from the browser Geolocation API.
 * Uses high accuracy to get a GPS-quality result when available.
 */
function getNavigatorLocation(): Promise<GeolocationPosition> {
    return new Promise<GeolocationPosition>((resolve, reject) => {
        if (!navigator.geolocation) {
            reject(new Error('Geolocation is not supported by this browser.'));
            return;
        }

        navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            maximumAge: CACHE_TTL_MS,
            timeout: 10_000,
        });
    });
}

type GeolocationPermissionState = 'granted' | 'prompt' | 'denied' | 'unsupported';

async function getGeolocationPermissionState(): Promise<GeolocationPermissionState> {
    if (!navigator.permissions?.query) {
        return 'unsupported';
    }

    try {
        const status = await navigator.permissions.query({ name: 'geolocation' });
        return status.state;
    } catch {
        return 'unsupported';
    }
}

/**
 * Module-level singleton that resolves the user's location exactly once per
 * browser session and shares the result with every subscriber. It removes the
 * per-component duplication that occurs when many mounted components each run
 * their own IP lookup and browser geolocation attempt.
 *
 * Resolution strategy (mirrors the previous per-hook behavior):
 *
 * 1. A valid sessionStorage value is used as the initial snapshot.
 * 2. If there is no cached value, the IP-geolocation endpoint is requested, with
 *    a bounded retry.
 * 3. Browser `navigator.geolocation` is attempted in parallel and, when it
 *    succeeds, overrides an IP-, cache-, or fallback-sourced value with a more
 *    accurate one.
 * 4. If every IP attempt fails and no other location is known, the default
 *    location is applied so consumers always end up with usable coordinates
 *    instead of waiting on `null` forever.
 *
 * Consumers read the value through `useSyncExternalStore` via `useUserLocation`.
 */

/** IP attempts made in a single resolution round (initial request + retries). */
const MAX_IP_ATTEMPTS_PER_ROUND = 2;

/** Delay before retrying a failed IP lookup within a round. */
const IP_RETRY_DELAY_MS = 1_000;

/** Hard ceiling on IP attempts for the whole session, across all rounds. */
const MAX_IP_ATTEMPTS_PER_SESSION = 3;

/** The current best-known location shared across all subscribers. */
let currentLocation: UserLocation | null = null;

/** Whether the initial cache read has happened (kept stable for snapshots). */
let hasReadCache = false;

/** IP attempts made so far this session; bounded by `MAX_IP_ATTEMPTS_PER_SESSION`. */
let ipAttempts = 0;

/** Whether an IP lookup (or its scheduled retry) is currently outstanding. */
let isIpRequestInFlight = false;

/** Ensures the navigator attempt is made at most once per session. */
let navigatorAttempted = false;

/** Subscribers notified whenever the shared location changes. */
const listeners = new Set<() => void>();

/**
 * Reads the session cache defensively. Returns `null` when storage is
 * unavailable (e.g. server-side rendering) so importing this module never
 * throws outside the browser.
 */
function readCachedLocation(): UserLocation | null {
    if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
        return null;
    }

    return getCachedLocation();
}

/** Performs the one-time initial cache read, keeping the snapshot reference stable. */
function ensureCacheRead(): void {
    if (hasReadCache) return;

    hasReadCache = true;
    currentLocation = readCachedLocation();
}

function emitChange(): void {
    for (const listener of listeners) {
        listener();
    }
}

/**
 * Updates the shared location and notifies subscribers. Persists to the session
 * cache unless `shouldCache` is false (used for the default-location fallback,
 * which must not outlive the failed lookup that produced it).
 */
function setLocation(next: UserLocation, shouldCache = true): void {
    currentLocation = next;

    if (shouldCache) {
        setCachedLocation(next);
    }

    emitChange();
}

/**
 * Applies the default location so consumers gated on a non-null location can
 * proceed when resolution fails. Never overwrites a real location, and is
 * deliberately not cached so the next page load retries the real lookups.
 */
function applyFallbackLocation(): void {
    if (currentLocation) return;

    const defaultLocation = getLocationWithFallback();

    setLocation(
        {
            source: 'fallback',
            latitude: defaultLocation.latitude,
            longitude: defaultLocation.longitude,
            location: defaultLocation.location,
            isInUS: defaultLocation.isInUS,
        },
        false
    );
}

/**
 * Fires the IP-geolocation lookup as a fast, permission-free fallback. The
 * result is ignored if a navigator-sourced location has already been resolved.
 *
 * Failures are retried a bounded number of times; when the retries are spent the
 * default location is applied so consumers are never left waiting on `null`.
 */
function resolveFromIp(attemptsThisRound = 0): void {
    if (isIpRequestInFlight || ipAttempts >= MAX_IP_ATTEMPTS_PER_SESSION) return;

    isIpRequestInFlight = true;
    ipAttempts += 1;

    getGeoLocationFromIp()
        .then((ipData) => {
            isIpRequestInFlight = false;

            if (!ipData) {
                applyFallbackLocation();
                return;
            }

            // A navigator result is more accurate; don't overwrite it.
            if (currentLocation?.source === 'navigator') return;

            const validatedLocation = getLocationWithFallback(ipData);

            setLocation({
                source: 'ip',
                latitude: validatedLocation.latitude,
                longitude: validatedLocation.longitude,
                location: validatedLocation.location,
                isInUS: validatedLocation.isInUS,
            });
        })
        .catch(() => {
            isIpRequestInFlight = false;

            const canRetry =
                attemptsThisRound + 1 < MAX_IP_ATTEMPTS_PER_ROUND &&
                ipAttempts < MAX_IP_ATTEMPTS_PER_SESSION;

            if (canRetry) {
                isIpRequestInFlight = true;

                setTimeout(() => {
                    isIpRequestInFlight = false;
                    resolveFromIp(attemptsThisRound + 1);
                }, IP_RETRY_DELAY_MS);

                return;
            }

            // Retries are spent — fall back so consumers can render something.
            applyFallbackLocation();
        });
}

/**
 * Attempts browser geolocation for a more accurate result. Skips the request
 * when permission is already denied and swallows all errors so the current
 * location remains in use.
 */
function attemptNavigator(): void {
    if (navigatorAttempted) return;

    navigatorAttempted = true;

    getGeolocationPermissionState()
        .then((permissionState) => {
            if (permissionState === 'denied') {
                return null;
            }

            return getNavigatorLocation();
        })
        .then((position) => {
            if (!position) return;

            const validatedLocation = getLocationWithFallback({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
            });

            setLocation({
                source: 'navigator',
                latitude: validatedLocation.latitude,
                longitude: validatedLocation.longitude,
                location: validatedLocation.location,
                isInUS: validatedLocation.isInUS,
            });
        })
        .catch(() => {
            // Navigator failed — keep whatever location we already have.
        });
}

/**
 * Kicks off IP + navigator resolution. Safe to call on every subscribe: the IP
 * lookup is skipped once a real location is known, and a component mounting after
 * a failed round gets another attempt (up to the per-session ceiling) so a
 * transient network failure isn't permanent.
 */
function ensureResolution(): void {
    ensureCacheRead();

    // Request IP geolocation when nothing is known, or when all we have is the
    // default-location fallback from an earlier failure.
    if (!currentLocation || currentLocation.source === 'fallback') {
        resolveFromIp();
    }

    // Attempt navigator in parallel unless we already have a navigator result.
    if (currentLocation?.source !== 'navigator') {
        attemptNavigator();
    }
}

/**
 * Subscribes to shared user-location changes. The first subscriber triggers
 * one-time resolution. Returns an unsubscribe function.
 */
export function subscribeUserLocation(listener: () => void): () => void {
    listeners.add(listener);
    ensureResolution();

    return () => {
        listeners.delete(listener);
    };
}

/**
 * Returns the current shared location. The reference is stable between changes,
 * which `useSyncExternalStore` requires to avoid render loops.
 */
export function getUserLocationSnapshot(): UserLocation | null {
    ensureCacheRead();
    return currentLocation;
}

/** Server snapshot for SSR/hydration safety; no location is available there. */
export function getUserLocationServerSnapshot(): UserLocation | null {
    return null;
}

/**
 * Test-only. Resets the singleton to a clean state: clears subscribers, resets
 * the resolution guards, and re-reads the session cache. This must live in the
 * module because it mutates private singleton state that app code cannot reach.
 */
export function __resetUserLocationStore(): void {
    listeners.clear();
    ipAttempts = 0;
    isIpRequestInFlight = false;
    navigatorAttempted = false;
    hasReadCache = true;
    currentLocation = readCachedLocation();
}
